import { useState, useEffect } from "react";
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaSave, FaTimes,
    FaUserShield, FaGlobe, FaBuilding, FaUser, FaCopy
} from "react-icons/fa";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { toast } from "react-toastify";
import FormularioPerfil, { ALCANCES } from "@/components/FormularioPerfil";

export default function GestionPerfiles() {
    const [perfiles, setPerfiles] = useState([]);
    const [filtro, setFiltro] = useState("");
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [perfilEditando, setPerfilEditando] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showJsonPreview, setShowJsonPreview] = useState(false);

    // Estado del formulario
    const [formData, setFormData] = useState({
        nombre: "",
        configuracion: {
            ver_todo: false,
            alcance: "cliente",
            modulosVisibles: [],
            permisos: {},
            menus_sidebar: []
        }
    });

    useEffect(() => {
        cargarPerfiles();
    }, []);

    const cargarPerfiles = async () => {
        setLoading(true);
        try {
            const response = await axios.get(API_ROUTES.perfiles);
            let data = Array.isArray(response.data) ? response.data : [];

            data = data.map(perfil => ({
                ...perfil,
                configuracion: typeof perfil.configuracion === 'string'
                    ? JSON.parse(perfil.configuracion)
                    : perfil.configuracion || {}
            }));

            setPerfiles(data);
        } catch (error) {
            console.error("Error al cargar perfiles:", error);
            toast.error("Error al cargar perfiles");
            setPerfiles([]);
        } finally {
            setLoading(false);
        }
    };

    const abrirModal = async (perfil = null) => {
        if (perfil) {
            setModoEdicion(true);
            setPerfilEditando(perfil);

            try {
                const response = await axios.get(API_ROUTES.perfilDetalle(perfil.id));
                const perfilCompleto = response.data;

                let config = perfilCompleto.configuracion || perfilCompleto.config_json;
                if (typeof config === 'string') {
                    config = JSON.parse(config);
                }

                setFormData({
                    nombre: perfilCompleto.nombre,
                    configuracion: {
                        ver_todo: config?.ver_todo || false,
                        alcance: config?.alcance || "cliente",
                        modulosVisibles: config?.modulosVisibles || [],
                        permisos: config?.permisos || {},
                        menus_sidebar: config?.menus_sidebar || []
                    }
                });
            } catch (error) {
                console.error("Error al cargar perfil:", error);
                toast.error("Error al cargar los datos del perfil");
                return;
            }
        } else {
            setModoEdicion(false);
            setPerfilEditando(null);
            setFormData({
                nombre: "",
                configuracion: {
                    ver_todo: false,
                    alcance: "cliente",
                    modulosVisibles: [],
                    permisos: {},
                    menus_sidebar: []
                }
            });
        }
        setShowJsonPreview(false);
        setModalAbierto(true);
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setModoEdicion(false);
        setPerfilEditando(null);
        setShowJsonPreview(false);
    };

    const guardarPerfil = async () => {
        if (!formData.nombre.trim()) {
            toast.error("El nombre del perfil es requerido");
            return;
        }

        setLoading(true);
        try {
            const perfilParaEnviar = {
                nombre: formData.nombre,
                configuracion: JSON.stringify(formData.configuracion)
            };

            if (modoEdicion) {
                await axios.put(API_ROUTES.perfilUpdate(perfilEditando.id), perfilParaEnviar);
                toast.success("Perfil actualizado correctamente");
            } else {
                await axios.post(API_ROUTES.perfilCreate, perfilParaEnviar);
                toast.success("Perfil creado correctamente");
            }

            await cargarPerfiles();
            cerrarModal();
        } catch (error) {
            console.error("Error al guardar perfil:", error);
            toast.error(error.response?.data?.message || "Error al guardar el perfil");
        } finally {
            setLoading(false);
        }
    };

    const eliminarPerfil = async (id) => {
        if (!confirm("¿Estás seguro de eliminar este perfil?")) return;

        setLoading(true);
        try {
            await axios.delete(API_ROUTES.perfilDelete(id));
            toast.success("Perfil eliminado correctamente");
            await cargarPerfiles();
        } catch (error) {
            console.error("Error al eliminar perfil:", error);
            toast.error(error.response?.data?.message || "Error al eliminar el perfil");
        } finally {
            setLoading(false);
        }
    };

    const duplicarPerfil = (perfil) => {
        setFormData({
            nombre: `${perfil.nombre} (Copia)`,
            configuracion: { ...perfil.configuracion }
        });
        setModoEdicion(false);
        setPerfilEditando(null);
        setShowJsonPreview(false);
        setModalAbierto(true);
    };

    const perfilesFiltrados = perfiles.filter(p =>
        p.nombre.toLowerCase().includes(filtro.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl shadow-xl p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => window.history.back()}
                                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                                title="Regresar a Permisos de Menús"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                                    <FaUserShield className="text-4xl" />
                                    Gestión de Perfiles
                                </h1>
                                <p className="text-cyan-100 mt-2">
                                    Administra los perfiles de permisos del sistema
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => abrirModal()}
                            className="bg-white text-cyan-600 px-6 py-3 rounded-lg font-semibold hover:bg-cyan-50 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                            <FaPlus /> Nuevo Perfil
                        </button>
                    </div>
                </div>

                {/* Buscador */}
                <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                    <div className="relative">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar perfil por nombre..."
                            value={filtro}
                            onChange={(e) => setFiltro(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Lista de perfiles */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {perfilesFiltrados.map((perfil) => {
                        const alcance = ALCANCES.find(a => a.value === perfil.configuracion?.alcance);
                        return (
                            <div
                                key={perfil.id}
                                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all border-2 border-gray-100 hover:border-cyan-300"
                            >
                                <div className="p-4 border-b border-gray-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-xl font-bold text-gray-800">
                                            {perfil.nombre}
                                        </h3>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => duplicarPerfil(perfil)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Duplicar perfil"
                                            >
                                                <FaCopy size={16} />
                                            </button>
                                            <button
                                                onClick={() => abrirModal(perfil)}
                                                className="p-2 text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                                                title="Editar perfil"
                                            >
                                                <FaEdit size={16} />
                                            </button>
                                            <button
                                                onClick={() => eliminarPerfil(perfil.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Eliminar perfil"
                                            >
                                                <FaTrash size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {alcance?.label || 'No definido'}
                                    </div>
                                </div>

                                <div className="p-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                                            <div className="text-2xl font-bold text-blue-600">
                                                {perfil.configuracion?.modulosVisibles?.length || 0}
                                            </div>
                                            <div className="text-xs text-gray-600">Módulos</div>
                                        </div>
                                        <div className="bg-green-50 rounded-lg p-3 text-center">
                                            <div className="text-2xl font-bold text-green-600">
                                                {perfil.configuracion?.menus_sidebar?.length || 0}
                                            </div>
                                            <div className="text-xs text-gray-600">Menús</div>
                                        </div>
                                    </div>

                                    {perfil.configuracion?.ver_todo && (
                                        <div className="mt-3 inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                                            <span>👁️</span> Ver Todo
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {perfilesFiltrados.length === 0 && (
                    <div className="text-center py-12 bg-white rounded-xl shadow-md">
                        <div className="text-6xl mb-4">🔍</div>
                        <p className="text-gray-500">No se encontraron perfiles</p>
                    </div>
                )}
            </div>

            {/* Modal de edición/creación */}
            {modalAbierto && (
                <div
                    className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            cerrarModal();
                        }
                    }}
                >
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Header del modal */}
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6 flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                                <FaUserShield />
                                {modoEdicion ? "Editar Perfil" : "Nuevo Perfil"}
                            </h2>
                            <button
                                onClick={cerrarModal}
                                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                            >
                                <FaTimes size={24} />
                            </button>
                        </div>

                        {/* Contenido del modal - Usando FormularioPerfil */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <FormularioPerfil
                                formData={formData}
                                onChange={setFormData}
                                showNombreField={true}
                                showJsonPreview={showJsonPreview}
                                onJsonPreviewToggle={setShowJsonPreview}
                            />
                        </div>

                        {/* Footer del modal */}
                        <div className="border-t bg-gray-50 p-6 flex justify-end gap-3">
                            <button
                                onClick={cerrarModal}
                                className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
                            >
                                <FaTimes /> Cancelar
                            </button>
                            <button
                                onClick={guardarPerfil}
                                disabled={loading}
                                className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50"
                            >
                                <FaSave /> {loading ? "Guardando..." : "Guardar Perfil"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}